import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, Users, UserPlus, FileSpreadsheet, Loader2 } from "lucide-react";

const DISCIPLINES = [
  ["autre", "Autre / à préciser"], ["muay_thai_kickboxing", "Kickboxing / Muay Thaï"],
  ["jiu_jitsu_bresilien", "Jiu-Jitsu Brésilien"], ["jujutsu_japonais", "Jiu-jitsu Can Ryu"],
  ["power_kicker", "Powerkick HER"],
];

// Parse simple d'un CSV ZenPlanner (Name, Email, Phone, Status).
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const rows = [];
  let header = null;
  for (const line of lines) {
    const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    if (!header) {
      if (cols.map((c) => c.toLowerCase()).includes("name")) { header = cols.map((c) => c.toLowerCase()); }
      continue;
    }
    const rec = {};
    header.forEach((h, i) => (rec[h] = cols[i] || ""));
    if (rec.name) rows.push({ name: rec.name, email: rec.email || "", phone: rec.phone || "", status: rec.status || "" });
  }
  return rows;
}

export default function ZenPlannerImport() {
  const [rows, setRows] = useState([]);
  const [discipline, setDiscipline] = useState("autre");
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [prospects, setProspects] = useState([]);

  const loadProspects = async () => {
    try { const { data } = await api.get("/members/import-zenplanner/prospects"); setProspects(data.prospects || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadProspects(); }, []);

  const onFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCsv(e.target.result);
      setRows(parsed); setPreview(null);
      toast.success(`${parsed.length} lignes lues`);
    };
    reader.readAsText(file);
  };

  const run = async (dry) => {
    if (!rows.length) { toast.error("Charge d'abord un fichier CSV"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/members/import-zenplanner", { rows, default_discipline: discipline, dry_run: dry });
      setPreview(data);
      toast.success(dry ? "Aperçu calculé" : "Import terminé ✓");
      if (!dry) loadProspects();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import"); }
    finally { setRunning(false); }
  };

  const studentsCount = rows.filter((r) => (r.status || "").toLowerCase() === "student").length;
  const prospectRows = rows.length - studentsCount;

  return (
    <div className="p-6 text-zinc-100 max-w-5xl mx-auto" data-testid="zenplanner-import">
      <h1 className="text-3xl font-bold flex items-center gap-2"><FileSpreadsheet className="text-emerald-400" /> Import membres ZenPlanner</h1>
      <p className="text-zinc-400 text-sm mt-1">Importe un export ZenPlanner (colonnes : Name, Email, Phone, Status). Les « Student » deviennent des membres actifs, les autres sont classés en prospects. Dédoublonnage par nom complet.</p>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mt-5 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <label className="flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-emerald-500" data-testid="csv-file-label">
            <Upload size={16} /> Choisir un fichier CSV
            <input type="file" accept=".csv" className="hidden" data-testid="csv-file-input"
              onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
          </label>
          <div>
            <label className="text-xs text-zinc-400 block">Discipline par défaut (nouveaux membres)</label>
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="discipline-select"
              className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100">
              {DISCIPLINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        {rows.length > 0 && (
          <div className="flex flex-wrap gap-4 text-sm" data-testid="import-counts">
            <span className="text-zinc-300"><Users size={14} className="inline mr-1 text-emerald-400" /> {studentsCount} membres (Student)</span>
            <span className="text-zinc-300"><UserPlus size={14} className="inline mr-1 text-sky-400" /> {prospectRows} prospects</span>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => run(true)} disabled={running || !rows.length} data-testid="dry-run-btn"
            className="flex items-center gap-2 bg-zinc-800 border border-zinc-600 text-zinc-200 px-4 py-2 rounded-lg hover:bg-zinc-700 disabled:opacity-50 text-sm">
            {running ? <Loader2 size={15} className="animate-spin" /> : null} Aperçu (sans écrire)</button>
          <button onClick={() => run(false)} disabled={running || !rows.length} data-testid="import-btn"
            className="flex items-center gap-2 bg-emerald-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-emerald-500 disabled:opacity-50 text-sm">
            {running ? <Loader2 size={15} className="animate-spin" /> : null} Lancer l'import</button>
        </div>

        {preview && (
          <div className="rounded-lg border border-zinc-700 bg-zinc-950 p-4 text-sm" data-testid="import-result">
            <div className="font-semibold text-emerald-300 mb-2">{preview.dry_run ? "Aperçu" : "Résultat de l'import"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><div className="text-2xl font-bold text-emerald-400">{preview.members_created}</div><div className="text-xs text-zinc-400">membres créés</div></div>
              <div><div className="text-2xl font-bold text-emerald-300">{preview.members_updated}</div><div className="text-xs text-zinc-400">membres mis à jour</div></div>
              <div><div className="text-2xl font-bold text-sky-400">{preview.prospects_created}</div><div className="text-xs text-zinc-400">prospects créés</div></div>
              <div><div className="text-2xl font-bold text-sky-300">{preview.prospects_updated}</div><div className="text-xs text-zinc-400">prospects mis à jour</div></div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-semibold flex items-center gap-2"><UserPlus size={18} className="text-sky-400" /> Prospects importés ({prospects.length})</h2>
        <div className="mt-2 rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead className="bg-zinc-900 text-zinc-400"><tr><th className="text-left px-3 py-2">Nom</th><th className="text-left px-3 py-2">Courriel</th><th className="text-left px-3 py-2">Téléphone</th></tr></thead>
            <tbody>
              {prospects.slice(0, 300).map((p) => (
                <tr key={p.id} className="border-t border-zinc-800"><td className="px-3 py-1.5">{p.full_name}</td><td className="px-3 py-1.5 text-zinc-400">{p.email || "—"}</td><td className="px-3 py-1.5 text-zinc-400">{p.phone || "—"}</td></tr>
              ))}
              {prospects.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-zinc-500">Aucun prospect importé.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
